/* eslint-disable no-unused-vars */
import { useState, Fragment, useEffect } from 'react'
import { Input, Button, message, Form } from 'antd'
import { CheckOutlined, InfoCircleOutlined, CloseCircleOutlined, WarningOutlined, LeftOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons'
import intl from 'react-intl-universal'
import SvgIcon from '@/components/svgIcon'
import VerifyCodeInput from '../verifyCodeInput'
import PasswordStrength from '@/components/passwordStrength'
import { passwordSecurityLevel, Action } from '@/config/default'
import { validNewPsd } from '@/utils/validator'
import './index.scss'
import { getUserMobileApi, checkSMSVerificationCodeApi, getSMSVerificationCodeApi } from '@/services/common'
import { resetPsdApi } from '@/services/login'
import { Encrypt } from '@/utils/aes'
import type { UserMobileInfoResponse } from '@/services/common/types'

interface ForgetPwdProps {
  onBack: () => void
}

const maskPhone = (phone: string) => {
  if (!phone || phone.length < 8) return phone
  return `${phone.slice(0, 3)}****${phone.slice(-4)}`
}

function ForgetPwd ({ onBack }: ForgetPwdProps) {
  const [form] = Form.useForm()
  const newPassword: string = Form.useWatch('newPassword', form)
  const { validateFields, setFieldsValue } = form

  const steps = [
    { title: intl.get('login.inputAccountStep') },
    { title: intl.get('login.identityVerificationStep') },
    { title: intl.get('login.resetPasswordStep') }
  ]

  const [step, setStep] = useState(1)
  const [step1Loading, setStep1Loading] = useState(false)
  const [step2Loading, setStep2Loading] = useState(false)
  const [step3Loading, setStep3Loading] = useState(false)
  // 第一步输入的企业号、个人账号
  const [step1Info, setStep1Info] = useState<{ enterpriseCode: string; account: string } | null>(null)
  // 第一步获取到的账号信息
  const [accountInfo, setAccountInfo] = useState<UserMobileInfoResponse | null>(null)

  // 第三步
  const [newVisible, setNewVisible] = useState(false)
  const [confirmVisible, setConfirmVisible] = useState(false)
  const [passwordFocused, setPasswordFocused] = useState(false)

  // 自定义校验规则
  const validateVerifyCode = (rule, value, callback) => {
    if (value && !/^\d+$/.test(value)) {
      return callback(new Error(intl.get('login.pleaseEnterCorrectVerifyCodeFormat')))
    }
    callback()
  }

  // 校验新密码
  const validateNewPassword = (rule, value, callback) => {
    const securityLevel = accountInfo && accountInfo.securityLevel ? accountInfo.securityLevel : passwordSecurityLevel.LOW
    const min = accountInfo && accountInfo.passwordLengthMin ? accountInfo.passwordLengthMin : 6
    const max = accountInfo && accountInfo.passwordLengthMax ? accountInfo.passwordLengthMax : 20
    if (value && !validNewPsd(value, securityLevel, min, max)) {
      return callback(new Error(intl.get('login.newPasswordNotMeetRules')))
    }
    callback()
  }
  // 校验确认密码
  const validateConfirmPassword = (rule, value, callback) => {
    if (value && value !== newPassword) {
      return callback(new Error(intl.get('login.passwordsNotMatch')))
    }
    callback()
  }

  // 输入账号下一步(step1 -> step2)
  const handleStep1Next = async () => {
    try {
      const values = await validateFields(['enterpriseCode', 'account'])
      setStep1Loading(true)
      setStep1Info(values)
      const res = await getUserMobileApi({
        enterpriseAccount: values.enterpriseCode,
        account: values.account
      })
      if (res && res.status) {
        setAccountInfo(res)
        setStep(2)
      } else {
        message.error(res.errorMsg)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setStep1Loading(false)
    }
  }

  // 获取验证码
  const handleSendVerifyCode = async () => {
    getSMSVerificationCodeApi({
      enterpriseCode: step1Info.enterpriseCode,
      mobile: accountInfo.mobile,
      action: Action.RESET_PASSWORD
    })
  }

  // 校验验证码(step2 -> step3)
  const handleStep2Next = async () => {
    try {
      setStep2Loading(true)
      const values = await validateFields(['verifyCode'])
      await checkSMSVerificationCodeApi({
        enterpriseCode: step1Info.enterpriseCode,
        mobile: accountInfo.mobile,
        captcha: values.verifyCode
      })
      setStep(3)
    } catch (error) {
      console.log(error)
    } finally {
      setStep2Loading(false)
    }
  }

  // 重置密码(step3 -> done)
  const handleReset = async () => {
    try {
      const values = await validateFields(['newPassword', 'confirmPassword'])
      setStep3Loading(true)
      await resetPsdApi({
        enterpriseCode: step1Info.enterpriseCode,
        account: step1Info.account,
        mobile: accountInfo.mobile,
        password: Encrypt(values.newPassword)
      })
      message.success(intl.get('login.passwordResetSuccess'))
      onBack && onBack()
    } catch (error) {
      console.log(error)
    } finally {
      setStep3Loading(false)
    }
  }

  const handlePrev = () => {
    if (step > 1) {
      setStep(step - 1)
    } else {
      onBack && onBack()
    }
  }
  useEffect(() => {
    if (step === 1) {
      if (step1Info && step1Info.enterpriseCode && step1Info.account) {
        setFieldsValue({
          enterpriseCode: step1Info.enterpriseCode,
          account: step1Info.account
        })
      }
    }
  }, [step])

  const getLineClass = (lineIndex: number) => {
    // lineIndex 为第 lineIndex 条连线，连接第 lineIndex 步与第 lineIndex + 1 步
    if (step > lineIndex + 1) return 'done'
    if (step === lineIndex + 1) return 'active'
    return ''
  }

  return (
    <Form layout='vertical' className='loginForm-forgetPwd' form={form}>
      {/* 步骤条 */}
      <div className='forgetPwd-steps'>
        {steps.map((item, index) => {
          const stepNum = index + 1
          const isDone = step > stepNum
          const isActive = step === stepNum
          return (
            <Fragment key={item.title}>
              <div className='forgetPwd-step'>
                <div
                  className={`forgetPwd-step-node ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}
                >
                  {isDone ? <CheckOutlined /> : stepNum}
                </div>
                <div
                  className={`forgetPwd-step-title ${isDone || isActive ? 'active' : ''}`}
                >
                  {item.title}
                </div>
              </div>
              {index < steps.length - 1 && (
                <div className={`forgetPwd-step-line ${getLineClass(index + 1)}`} />
              )}
            </Fragment>
          )
        })}
      </div>

      {/* 标题 */}
      {step === 1 && (
        <Fragment>
          <div className='forgetPwd-title'>{intl.get('login.accountInfoVerify')}</div>
          <div className='forgetPwd-subtitle'>{intl.get('login.pleaseEnterEnterpriseCodeAndAccount')}</div>
        </Fragment>
      )}
      {step === 2 && (
        <Fragment>
          <div className='forgetPwd-title'>{intl.get('login.securityVerification')}</div>
          <div className='forgetPwd-subtitle'>{intl.get('login.pleaseCompleteIdentityVerification')}</div>
        </Fragment>
      )}
      {step === 3 && (
        <Fragment>
          <div className='forgetPwd-title'>{intl.get('login.setNewPassword')}</div>
          <div className='forgetPwd-subtitle'>{intl.get('login.pleaseEnterYourNewPassword')}</div>
        </Fragment>
      )}

      {/* 第一步：输入账号 */}
      {step === 1 && (
        <Fragment>
          <Form.Item
            className='loginForm-input-wrap'
            label={intl.get('login.enterpriseCode')}
            name="enterpriseCode"
            rules={[{ required: true, message: intl.get('login.pleaseEnterEnterpriseCode') }]}
          >
            <Input
              className='loginForm-input'
              placeholder={intl.get('login.pleaseEnterEnterpriseCode')}
              prefix={<SvgIcon name='logoEnterprise' />}
            />
          </Form.Item>
          <Form.Item
            className='loginForm-input-wrap'
            label={intl.get('login.personalAccount')}
            name="account"
            rules={[{ required: true, message: intl.get('login.pleaseEnterPersonalAccount') }]}
          >
            <Input
              className='loginForm-input'
              placeholder={intl.get('login.pleaseEnterPersonalAccount')}
              prefix={<SvgIcon name='loginAccount' />}
            />
          </Form.Item>
          <div className='forgetPwd-tip'>
            <InfoCircleOutlined />
            <span>{intl.get('login.systemWillAutoFillPhone')}</span>
          </div>
          <Button
            className='loginForm-submit'
            type='primary'
            block
            loading={step1Loading}
            onClick={handleStep1Next}
          >
            {intl.get('login.Next')}
          </Button>
        </Fragment>
      )}

      {/* 第二步：身份验证 */}
      {step === 2 && (
        <Fragment>
          {(accountInfo && accountInfo.mobile) ? (
            <div className='forgetPwd-info'>
              <InfoCircleOutlined />
              <div className='forgetPwd-info-content'>
                <div>{intl.get('login.systemAutoFilledContact')}</div>
                <div className='forgetPwd-info-phone'>
                  {intl.get('login.phoneNumber')}：{maskPhone(accountInfo.mobile)}
                </div>
                <div>{intl.get('login.verifyCodeWillBeSentToPhone')}</div>
              </div>
            </div>
          ) : (
            <div className='forgetPwd-error'>
              <CloseCircleOutlined />
              <span>{intl.get('login.noPhoneBoundWarning')}</span>
            </div>
          )}

          <Form.Item
            className='loginForm-input-wrap'
            label={intl.get('login.verifyCodeLabel')}
            name="verifyCode"
            rules={[
              { required: true, message: intl.get('login.pleaseEnterVerifyCode') },
              { validator: validateVerifyCode }
            ]}
          >
            <VerifyCodeInput
              onSend={handleSendVerifyCode}
              storageKey='forgetPwdVerifyCodeExpireAt'
              inputClassName='loginForm-input'
              disabled={!(accountInfo && accountInfo.mobile)}
            />
          </Form.Item>

          <div className='forgetPwd-warning'>
            <WarningOutlined />
            <span>{intl.get('login.needModifyPhoneContactAdmin')}</span>
          </div>

          <Button
            className='loginForm-submit'
            type='primary'
            block
            loading={step2Loading}
            disabled={!(accountInfo && accountInfo.mobile)}
            onClick={handleStep2Next}
          >
            {intl.get('login.Next')}
          </Button>
        </Fragment>
      )}

      {/* 第三步：重置密码 */}
      {step === 3 && (
        <Fragment>
          <Form.Item
            className='loginForm-input-wrap'
            label={intl.get('login.newPassword')}
            name="newPassword"
            rules={[
              { required: true, message: intl.get('login.pleaseEnterYourNewPassword') },
              { validator: validateNewPassword }
            ]}
          >
            <Input
              className='loginForm-input'
              type={newVisible ? 'text' : 'password'}
              placeholder={intl.get('login.pleaseEnterYourNewPassword')}
              prefix={<SvgIcon name='loginPassword' />}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              suffix={
                newVisible
                  ? <EyeOutlined className='loginForm-password-toggle' onClick={() => setNewVisible(!newVisible)} />
                  : <EyeInvisibleOutlined className='loginForm-password-toggle' onClick={() => setNewVisible(!newVisible)} />
              }
            />
          </Form.Item>
          <div className={`forgetPwd-passwordStrength-wrapper ${passwordFocused ? 'expanded' : ''}`}>
            <PasswordStrength
              className='forgetPwd-strength'
              passwordLevel={(accountInfo && accountInfo.securityLevel) ? accountInfo.securityLevel : passwordSecurityLevel.LOW}
              value={newPassword}
              min={(accountInfo && accountInfo.passwordLengthMin) ? accountInfo.passwordLengthMin : 6}
              max={(accountInfo && accountInfo.passwordLengthMax) ? accountInfo.passwordLengthMax : 20}
            />
          </div>
          <Form.Item
            className='loginForm-input-wrap'
            label={intl.get('login.confirmNewPassword')}
            name="confirmPassword"
            rules={[
              { required: true, message: intl.get('login.pleaseEnterNewPasswordAgain') },
              { validator: validateConfirmPassword }
            ]}
          >
            <Input
              className='loginForm-input'
              type={confirmVisible ? 'text' : 'password'}
              placeholder={intl.get('login.pleaseEnterNewPasswordAgain')}
              prefix={<SvgIcon name='loginPassword' />}
              suffix={
                confirmVisible
                  ? <EyeOutlined className='loginForm-password-toggle' onClick={() => setConfirmVisible(!confirmVisible)} />
                  : <EyeInvisibleOutlined className='loginForm-password-toggle' onClick={() => setConfirmVisible(!confirmVisible)} />
              }
            />
          </Form.Item>
          <Button
            className='loginForm-submit'
            type='primary'
            block
            loading={step3Loading}
            onClick={handleReset}
          >
            {intl.get('login.resetPassword')}
          </Button>
        </Fragment>
      )}

      <div className='forgetPwd-back' onClick={handlePrev}>
        <LeftOutlined />
        <span>{step === 1 ? intl.get('login.backToLoginPage') : intl.get('login.backToPreviousStep')}</span>
      </div>
    </Form>
  )
}

export default ForgetPwd